/**
 * 発話タブ（Issue #998。シミュレータの画面の発話区間のレビューを入力画面へ移した）。
 *   頭: LLM レビュー（実行・講評。`SpeechReview.js`）・数（発話・笑い・カット・提案）・提案の一括の適用 / 却下・再生の最小限（前の発話 / 再生・停止 / 次の発話）
 *   一覧: 発話区間 1 行 1 件（開始時刻順。今の区間を強調して見える所へ）。行を押すとその区間の頭へ（動画は止めない＝聞き直せる）。
 *         「採用」のチェックで採否（カットした区間は薄く・取り消し線）、「直す」で文を直す、LLM の提案は行の中で「適用」「却下」。
 * **採否を書き換える操作はこのタブの中だけ**（常に見えている再生バー・情報帯には置かない。押し間違いで黙って反転する。Issue #290）。
 * 書き込みは `speech.js` の `useSpeechSegments` が返す口（`review`）。PC は F キーで今の区間の採否（pcKeys.js）。
 */
import { useEffect, useRef, useState } from 'react';
import { btn } from './RespondTab';
import SpeechReview from './SpeechReview';
import { LAUGHTER_SPEAKER, proposalTargets, speakerOrder, speechCounts, speechLabel } from '../../utils/speechSegments';

const SPEAKER_COLORS = ['#4fc3ff', '#e9a045', '#7bd88f', '#e98fd0', '#c6d86a', '#ff7b7b'];
const LAUGHTER_COLOR = '#a58be0';

/** ms → m:ss.s */
export function fmtSpeechMs(ms) {
  const sec = Math.max(0, ms || 0) / 1000;
  return `${Math.floor(sec / 60)}:${(sec % 60).toFixed(1).padStart(4, '0')}`;
}

const chip = (variant = 'plain') => ({ ...btn(variant), minHeight: 32, padding: '4px 10px', fontSize: 13 });

function TextEdit({ initial, onCommit, onCancel }) {
  const [value, setValue] = useState(initial ?? '');
  return (
    <div data-testid="speech-edit" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
      <textarea data-testid="speech-edit-text" rows={2} value={value} autoFocus onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onCommit(value); }
          if (e.key === 'Escape') onCancel();
        }}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 15, background: '#0b1224', color: '#e8eefc', border: '1px solid #345', borderRadius: 8, padding: 6, userSelect: 'text', WebkitUserSelect: 'text' }} />
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" data-testid="speech-edit-save" style={chip('primary')} onClick={() => onCommit(value)}>保存</button>
        <button type="button" style={chip()} onClick={onCancel}>やめる</button>
      </div>
    </div>
  );
}

function SpeechRow({ seg, color, active, editing, busy, onSeek, onToggle, onEdit, onCommit, onCancel, onApply, onDismiss, rowRef }) {
  const enabled = seg.enabled !== false;
  return (
    <div ref={rowRef} data-testid="speech-row" data-segment-id={seg.id} data-active={active ? '1' : undefined} data-enabled={enabled ? '1' : '0'}
      style={{ display: 'flex', alignItems: 'stretch', borderLeft: `4px solid ${color}`, background: active ? '#1a2a4a' : '#121a2e', borderRadius: 8, overflow: 'hidden', scrollSnapAlign: 'start', flexShrink: 0 }}>
      <label title="残す（チェックを外すとカット）" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 48, cursor: 'pointer', borderRight: '1px solid #2a3552' }}>
        <input type="checkbox" data-testid="speech-row-enabled" aria-label="採用" checked={enabled} disabled={busy}
          onChange={(e) => onToggle(seg.id, e.target.checked)} style={{ width: 22, height: 22, cursor: 'pointer' }} />
      </label>
      <div style={{ flex: 1, minWidth: 0, padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button type="button" data-testid="speech-row-seek" onClick={() => onSeek(seg.start_ms)} disabled={editing}
          style={{ display: 'flex', flexDirection: 'column', gap: 2, background: 'none', border: 'none', padding: 0, color: '#e8eefc', textAlign: 'left', cursor: 'pointer', opacity: enabled ? 1 : 0.45 }}>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{fmtSpeechMs(seg.start_ms)}</span>
            <span style={{ color, fontWeight: 700 }}>{speechLabel(seg)}</span>
            {seg.source === 'laughter_detect' ? <span style={{ opacity: 0.7 }}>自動検出</span> : null}
            {!enabled ? <span style={{ color: '#f99' }}>カット</span> : null}
          </span>
          {editing ? null : (
            <span style={{ fontSize: 14, wordBreak: 'break-word', textDecoration: enabled ? 'none' : 'line-through' }}>{seg.text || '（文なし）'}</span>
          )}
        </button>
        {editing ? <TextEdit initial={seg.text} onCommit={(t) => onCommit(seg, t)} onCancel={onCancel} /> : null}
        {seg.proposed_text ? (
          <div data-testid="speech-row-proposal" style={{ marginTop: 4, padding: '4px 6px', borderRadius: 6, background: '#2a2410', border: '1px solid #a84' }}>
            <div style={{ fontSize: 11, color: '#e9c46a' }}>LLM の提案（文の直し）</div>
            <div style={{ fontSize: 14, color: '#f3d9a0', wordBreak: 'break-word' }}>{seg.proposed_text}</div>
            {seg.proposal_reason ? <div style={{ fontSize: 11, color: '#b9a37a', marginTop: 2 }}>{seg.proposal_reason}</div> : null}
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button type="button" data-testid="speech-row-apply" disabled={busy} style={chip('primary')} onClick={() => onApply(seg.id)}>適用</button>
              <button type="button" data-testid="speech-row-dismiss" disabled={busy} style={chip()} onClick={() => onDismiss(seg.id)}>却下</button>
            </div>
          </div>
        ) : null}
      </div>
      {seg.speaker !== LAUGHTER_SPEAKER && !editing ? (
        <button type="button" data-testid="speech-row-edit" disabled={busy} onClick={() => onEdit(seg)}
          style={{ ...btn('plain'), minWidth: 52, borderRadius: 0, border: 'none', borderLeft: '1px solid #2a3552', fontSize: 13 }}>直す</button>
      ) : null}
    </div>
  );
}

/**
 * @param review     useSpeechSegments の返り（segments・error・busy と書き込み）
 * @param activeIndex 今の区間の添字（review.segments の並びで）
 * @param game       GetDuelGameInfo の duel（id と speech_review_*）
 */
export default function SpeechTab({ review, activeIndex = -1, game = null, time = { paused: true }, hasVideo = false, hasPrev = false, hasNext = false,
  onSeek, onPrev, onNext, onPlayPause, onReviewed }) {
  const { segments, error, busy } = review;
  const [editingId, setEditingId] = useState(null);
  const counts = speechCounts(segments);
  const order = speakerOrder(segments);
  const colorOf = (seg) => (seg.speaker === LAUGHTER_SPEAKER ? LAUGHTER_COLOR : SPEAKER_COLORS[Math.max(0, order.indexOf(seg.speaker)) % SPEAKER_COLORS.length]);

  // 今の区間を見える所へ（直している間は動かさない）
  const activeRef = useRef(null);
  const activeId = segments[activeIndex]?.id ?? null;
  useEffect(() => {
    if (editingId != null) return;
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId, editingId]);

  const commit = async (seg, text) => {
    const t = (text ?? '').trim();
    setEditingId(null);
    if (t && t !== seg.text) await review.setText(seg.id, t);
  };
  const bulk = (label, run) => { if (window.confirm(`LLM の提案 ${proposalTargets(segments).length} 件をすべて${label}しますか？`)) run(); };

  return (
    <div data-testid="speech-tab" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ padding: '0 10px', flexShrink: 0, borderBottom: '1px solid #1c2440' }}>
        <SpeechReview duelProject={game} onReviewed={onReviewed} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '6px 0' }}>
          <span data-testid="speech-counts" style={{ fontSize: 12, opacity: 0.85 }}>
            発話 {counts.speech}{counts.laughter ? `・笑い ${counts.laughter}` : ''}・カット {counts.cut}
          </span>
          {counts.proposals > 0 ? (
            <>
              <span style={{ fontSize: 12, color: '#e9c46a' }}>LLM の提案 {counts.proposals} 件</span>
              <button type="button" data-testid="speech-apply-all" disabled={busy} style={chip('primary')} onClick={() => bulk('適用', review.applyAll)}>すべて適用</button>
              <button type="button" data-testid="speech-dismiss-all" disabled={busy} style={chip()} onClick={() => bulk('却下', review.dismissAll)}>すべて却下</button>
            </>
          ) : null}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button type="button" aria-label="前の発話" disabled={!hasVideo || !hasPrev} style={chip()} onClick={onPrev}>◀</button>
            <button type="button" data-testid="speech-toggle-play" aria-label={time.paused ? '再生' : '一時停止'} disabled={!hasVideo} style={chip('primary')} onClick={onPlayPause}>{time.paused ? '▶' : '❚❚'}</button>
            <button type="button" aria-label="次の発話" disabled={!hasVideo || !hasNext} style={chip()} onClick={onNext}>▶</button>
          </span>
        </div>
        {error ? (
          <div role="alert" data-testid="speech-error" style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#fcc', background: '#412', padding: '4px 8px', fontSize: 12, marginBottom: 6, borderRadius: 6 }}>
            <span style={{ flex: 1 }}>{error}</span>
            <button type="button" aria-label="閉じる" onClick={review.clearError} style={{ background: 'none', border: 'none', color: '#fcc', cursor: 'pointer' }}>✕</button>
          </div>
        ) : null}
      </div>
      <div data-testid="speech-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6, scrollSnapType: 'y mandatory', overscrollBehavior: 'contain', scrollPaddingTop: 10 }}>
        {segments.length === 0 ? <div style={{ fontSize: 13, opacity: 0.7 }}>発話区間がありません（フィールド生成の音声認識で作られます）</div> : null}
        {segments.map((seg, i) => (
          <SpeechRow key={seg.id ?? `${seg.speaker}:${seg.start_ms}`} seg={seg} color={colorOf(seg)} active={i === activeIndex} editing={editingId === seg.id} busy={busy}
            rowRef={i === activeIndex ? activeRef : null}
            onSeek={onSeek} onToggle={review.setEnabled} onEdit={(s) => setEditingId(s.id)} onCommit={commit} onCancel={() => setEditingId(null)}
            onApply={review.applyProposal} onDismiss={review.dismissProposal} />
        ))}
      </div>
    </div>
  );
}
